// SessionExpiredModal — shown when the access token expires (regular 401s).
// Friendly message + 5s countdown + auto-redirect to /login. User can click "Go to Login" to skip.
// Separate from SessionInvalidatedModal (which handles the explicit SESSION_INVALIDATED code
// for "you signed in on another device").
import React, { useEffect, useRef, useState } from 'react';
import { ClockCircleFilled } from '@ant-design/icons';
import { ThemeModal, ThemeButton } from '../../pages/Styles/Theme';
import { STORAGE_KEYS } from '../../utils/constants';

let showFn = null;
let isShown = false;

export const showSessionExpiredModal = () => {
  if (isShown) return;
  isShown = true;
  if (showFn) showFn();
};

const REDIRECT_SECONDS = 5;

const SessionExpiredModal = () => {
  const [open, setOpen] = useState(false);
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const intervalRef = useRef(null);

  useEffect(() => {
    showFn = () => {
      setSeconds(REDIRECT_SECONDS);
      setOpen(true);
    };
    return () => {
      showFn = null;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Start countdown when modal opens
  useEffect(() => {
    if (!open) return undefined;

    intervalRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          doRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const doRedirect = () => {
    setOpen(false);
    isShown = false;
    if (intervalRef.current) clearInterval(intervalRef.current);
    // Clear auth keys only (don't nuke sidebar_collapsed, theme, etc.)
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
    localStorage.removeItem('expiresAt');
    window.location.replace('/login');
  };

  return (
    <ThemeModal
      open={open}
      onCancel={doRedirect}
      closable={false}
      maskClosable={false}
      centered
      width={440}
      title={null}
      footer={null}
      zIndex={10001} /* above LottieLoader's 9999 overlay (user 2026-09-10) */
    >
      <div style={{ padding: '8px 4px 4px 4px', textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(46, 69, 218, 0.08)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <ClockCircleFilled style={{ fontSize: 30, color: '#2E45DA' }} />
        </div>

        <h3
          style={{
            margin: '0 0 10px 0',
            color: '#2C2926',
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          Your session has expired
        </h3>

        <p
          style={{
            margin: '0 0 20px 0',
            color: '#6D6A67',
            fontSize: 13,
            lineHeight: 1.6,
            padding: '0 12px',
          }}
        >
          For your security, your sign-in session has timed out. Please sign in
          again to continue.
        </p>

        <p
          style={{
            margin: '0 0 20px 0',
            color: '#2E45DA',
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          Redirecting to login in {seconds} second{seconds === 1 ? '' : 's'}...
        </p>

        <ThemeButton
          size="large"
          className="solidPurple"
          onClick={doRedirect}
          style={{ minWidth: 180 }}
        >
          Go to Login
        </ThemeButton>
      </div>
    </ThemeModal>
  );
};

export default SessionExpiredModal;
